/* =============================================================================
 *  AdsMonitorsCard — dashboard management surface for autonomous Google Ads
 *  monitors (Wave 3 P1).
 *
 *  Monitors are CREATED conversationally (ask Andrei: "monitor account X
 *  weekly for wasted spend") via the configureAdsMonitor tool, or via the
 *  owner-gated manageAdsMonitors endpoint. This card is the management half:
 *  list / pause / resume / delete / run-now, plus each monitor's last-run
 *  digest so you can see what the last pass found without leaving the
 *  dashboard. Proposals the monitors draft land in the approvals feed
 *  rendered next to this card (ChangeActivityPanel inbox mode).
 *
 *  Data flow:
 *   - monitor configs   → live client read of ads_monitors (firestore.rules
 *                         allows authed reads, forbids client writes) — so a
 *                         monitor Andrei creates mid-conversation appears here
 *                         immediately, and non-owner staff still see the list.
 *   - last-run digests  → live client read of ads_monitor_runs (read-only),
 *                         newest 30, first-per-monitor wins.
 *   - ALL mutations     → owner-gated HTTP endpoints (a non-owner clicking
 *                         pause/delete/run-now gets a clean 403 toast).
 * ============================================================================= */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, orderBy, query as fsQuery } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../../firebase';
import {
  AdsMonitor, deleteAdsMonitor, runAdsMonitorNow, upsertAdsMonitor,
} from '../../marketing/googleAdsApi';

interface MonitorRun {
  id: string;
  monitorId: string;
  ranAt?: any;
  digest?: string;
  skipped?: string;
  error?: string;
  findings?: Array<{ key: string; severity: string; title: string }>;
  proposals?: Array<{ changeSetId: string }>;
}

/** Millis from a Firestore Timestamp, an admin-SDK JSON `{_seconds}`, or a
 *  client `{seconds}` shape — the three forms a timestamp reaches us in. */
function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  const s = typeof ts.seconds === 'number' ? ts.seconds : ts._seconds;
  return typeof s === 'number' ? s * 1000 : 0;
}

function relTime(ms: number): string {
  if (!ms) return 'never';
  const diffMin = Math.floor((Date.now() - ms) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

interface AdsMonitorsCardProps {
  /** Navigate to the account page when a monitor's account name is clicked. */
  onSelectAccount?: (customerId: string) => void;
}

const AdsMonitorsCard: React.FC<AdsMonitorsCardProps> = ({ onSelectAccount }) => {
  const [monitors, setMonitors] = useState<AdsMonitor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [runs, setRuns] = useState<MonitorRun[]>([]);

  // Live monitor list — mutations below don't need to refetch; the snapshot
  // delivers the backend's writes (including lastRunAt bumps) as they land.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'ads_monitors'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as AdsMonitor));
        rows.sort((a, b) => String(a.accountName || a.accountId).localeCompare(String(b.accountName || b.accountId)));
        setError(null);
        setMonitors(rows);
      },
      (err) => { console.warn('[AdsMonitorsCard] monitors sub error:', err); setError(err?.message || 'subscription failed'); },
    );
    return () => unsub();
  }, []);

  // Latest runs, newest first — the first row per monitorId is its last run.
  useEffect(() => {
    const q = fsQuery(collection(db, 'ads_monitor_runs'), orderBy('ranAt', 'desc'), fsLimit(30));
    const unsub = onSnapshot(
      q,
      (snap) => setRuns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => { console.warn('[AdsMonitorsCard] runs sub error:', err); },
    );
    return () => unsub();
  }, []);

  const lastRunByMonitor = useMemo(() => {
    const map: Record<string, MonitorRun> = {};
    for (const r of runs) if (r.monitorId && !map[r.monitorId]) map[r.monitorId] = r;
    return map;
  }, [runs]);

  // Clear busy only if it's still OUR id — a finishing action must not
  // re-enable another row's in-flight buttons.
  const clearBusy = useCallback((id: string) => {
    setBusyId((cur) => (cur === id ? null : cur));
  }, []);

  const togglePause = useCallback(async (m: AdsMonitor) => {
    setBusyId(m.id);
    try {
      // The backend preserves omitted fields on update — only send the toggle.
      const r = await upsertAdsMonitor({ id: m.id, accountId: m.accountId, enabled: !m.enabled });
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(m.enabled ? 'Monitor paused.' : 'Monitor resumed.');
    } catch (e: any) {
      toast.error(`Couldn't update monitor: ${e?.message || 'error'}`);
    } finally {
      clearBusy(m.id);
    }
  }, [clearBusy]);

  const remove = useCallback(async (m: AdsMonitor) => {
    setBusyId(m.id);
    setConfirmDeleteId(null);
    try {
      const r = await deleteAdsMonitor(m.id);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(`Monitor for ${m.accountName || m.accountId} deleted.`);
    } catch (e: any) {
      toast.error(`Couldn't delete monitor: ${e?.message || 'error'}`);
    } finally {
      clearBusy(m.id);
    }
  }, [clearBusy]);

  const runNow = useCallback(async (m: AdsMonitor) => {
    setBusyId(m.id);
    try {
      const r = await runAdsMonitorNow(m.id);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success(`Ran "${m.accountName || m.accountId}": ${r.findings ?? 0} finding(s), ${r.proposed ?? 0} proposal(s) drafted.`);
    } catch (e: any) {
      toast.error(`Run failed: ${e?.message || 'error'}`);
    } finally {
      clearBusy(m.id);
    }
  }, [clearBusy]);

  return (
    <section className="home-monitors" aria-label="Ads monitors">
      <header className="home-monitors__head">
        <span className="home-monitors__title">⏲ ads monitors</span>
        {monitors && monitors.length > 0 && (
          <span className="home-monitors__count">{monitors.filter((m) => m.enabled).length}/{monitors.length} active</span>
        )}
      </header>

      {error && <div className="home-monitors__note">monitors unavailable: {error}</div>}
      {!error && monitors === null && <div className="home-monitors__note">loading monitors…</div>}
      {!error && monitors !== null && monitors.length === 0 && (
        <div className="home-monitors__note">
          no monitors yet — ask Andrei, e.g. <em>“monitor Acme Plumbing weekly for wasted spend and propose negatives”</em>
        </div>
      )}

      {(monitors || []).map((m) => {
        const last = lastRunByMonitor[m.id];
        const lastMs = last ? tsToMs(last.ranAt) : tsToMs(m.lastRunAt);
        const busy = busyId === m.id;
        return (
          <div key={m.id} className={`home-monitors__row${m.enabled ? '' : ' home-monitors__row--paused'}`}>
            <div className="home-monitors__row-head">
              {onSelectAccount ? (
                <button type="button" className="home-monitors__acct" title={`Open ${m.accountName || m.accountId}`} onClick={() => onSelectAccount(m.accountId)}>
                  {m.accountName || m.accountId}
                </button>
              ) : (
                <span className="home-monitors__acct" title={m.accountName || m.accountId}>{m.accountName || m.accountId}</span>
              )}
              <span className="home-monitors__cadence">{m.cadence}</span>
              <span className={`home-monitors__state home-monitors__state--${m.enabled ? 'on' : 'off'}`}>
                {m.enabled ? 'active' : 'paused'}
              </span>
            </div>
            <div className="home-monitors__watch" title={`Watching: ${(m.watchFor || []).map((w) => w.replace(/_/g, ' ')).join(', ')}`}>
              {(m.watchFor || []).map((w) => (
                <span key={w} className="home-monitors__chip">{w.replace(/_/g, ' ')}</span>
              ))}
              <span className={`home-monitors__chip home-monitors__chip--${m.mayProposeFixes ? 'fix' : 'surface'}`}>
                {m.mayProposeFixes ? 'drafts fixes' : 'surface only'}
              </span>
            </div>
            <div className="home-monitors__digest" title={last?.digest || ''}>
              {(() => {
                const acct = m.accountName || m.accountId;
                const raw = last?.skipped
                  ? `last run skipped: ${last.skipped}`
                  : last?.error
                    ? `last run failed: ${last.error}`
                    // No run doc in the newest-30 window but the monitor HAS
                    // run — say so instead of a contradictory 'no runs yet'.
                    : (last?.digest || (lastMs ? 'last run details rolled off' : 'no runs yet'));
                // The header already shows the account name; drop the digest's
                // redundant "<account>: " prefix so the 2-line clamp shows signal.
                return raw.startsWith(`${acct}:`) ? raw.slice(acct.length + 1).trim() : raw;
              })()}
              {lastMs > 0 && <span className="home-monitors__when"> · {relTime(lastMs)}</span>}
            </div>
            <div className="home-monitors__actions">
              <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => runNow(m)} title="Run detection for this account now">
                {busy ? '…' : '[run now]'}
              </button>
              <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => togglePause(m)}>
                {m.enabled ? '[pause]' : '[resume]'}
              </button>
              {confirmDeleteId === m.id ? (
                <>
                  <button type="button" className="home-monitors__btn home-monitors__btn--danger" disabled={busy} onClick={() => remove(m)}>[confirm delete]</button>
                  <button type="button" className="home-monitors__btn" disabled={busy} onClick={() => setConfirmDeleteId(null)}>[keep]</button>
                </>
              ) : (
                <button type="button" className="home-monitors__btn home-monitors__btn--danger" disabled={busy} onClick={() => setConfirmDeleteId(m.id)}>[delete]</button>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default AdsMonitorsCard;
